import { Event, SDK, ServerOptions } from '@commercetools/frontend-sdk';
import { Cart, ShippingMethod, Order } from 'shared/types/cart';
import { CartAndError } from 'shared/types/cart/Cart';
import { PriorPromoCodesResult } from 'shared/types/cart/PriorPromoCode';
import { Availability } from 'shared/types/product/InventoryQuery';
import { PaginatedResult } from 'shared/types/result';
import { SOASubmitOrderResponse } from 'shared/types/soa/order/place-order';
import { callActionMiddleware } from '@/helpers/utils/callActionHelper';
import { getSelectedDivision } from '@/helpers/utils/siteUtils';
import {
  AddCartItemAction,
  AdditionalDetailsAction,
  AdjustPriceAction,
  AdjustmentsAction,
  CancelOrderAction,
  CheckoutCartAction,
  CustomerCartAction,
  FreezeCartAction,
  GetAvailableCartShippingMethodsAction,
  GetCartAction,
  GetCartShippingMethodsAction,
  HoldCartAction,
  NoChargeOrderAction,
  QueryOrdersAction,
  RedeemDiscountCodeAction,
  RemoveCartItemAction,
  RemoveDiscountCodeAction,
  ResetCartAction,
  RevalidatecartAction,
  SetCartShippingMethodAction,
  ShippingDiscountAction,
  UnFreezeCartAction,
  UpdateCartAction,
  UpdateCartItemAction,
  UpdateLineItemAction,
  ValidatePaymentBatchAction,
  ReplaceCartLineItemAction,
  SubmitSoaOrderAction,
  LineItemsAvailibiltyAction,
  HandleShippingInsuranceAction,
  GetPriorPromoCodesAction,
} from '../../types/actions/CartActions';
import { ComposableCommerceEvents } from '../../types/events/ComposableCommerceEvents';
import {
  AddCartItemPayload,
  AdditionalDetailsPayload,
  AdjustPricePayload,
  AdjustmentPayload,
  GetCartShippingMethodsPayload,
  RedeemDiscountCodePayload,
  RemoveCartItemPayload,
  RemoveDiscountCodePayload,
  SetCartShippingMethodPayload,
  ShippingDiscountPayload,
  UpdateCartItemPayload,
  UpdateCartPayload,
  UpdateLineItem,
  ValidatePaymentBatchPayload,
  ReplaceCartLineItemPayload,
  GetCtOrderByIdPayload,
  ResetCartPayload,
  InsuranceItemPayload,
} from '../../types/payloads/CartPayloads';
import { QueryOrdersQuery, GetPriorPromoCodesQuery } from '../../types/queries/CartQueries';

export type CartActions = {
  getLineItemsAvailibilty: LineItemsAvailibiltyAction;
  getCart: GetCartAction;
  addItem: AddCartItemAction;
  removeItem: RemoveCartItemAction;
  updateItem: UpdateCartItemAction;
  updateCart: UpdateCartAction;
  getShippingMethods: GetCartShippingMethodsAction;
  getAvailableShippingMethods: GetAvailableCartShippingMethodsAction;
  setShippingMethod: SetCartShippingMethodAction;
  redeemDiscountCode: RedeemDiscountCodeAction;
  removeDiscountCode: RemoveDiscountCodeAction;
  checkout: CheckoutCartAction;
  queryOrders: QueryOrdersAction;
  applyShippingDiscount: ShippingDiscountAction;
  applyAdjustments: AdjustmentsAction;
  freezeCart: FreezeCartAction;
  unfreezeCart: UnFreezeCartAction;
  adjustLineItemPrice: AdjustPriceAction;
  addAdditionalDetails: AdditionalDetailsAction;
  cancelOrder: CancelOrderAction;
  getCartsByOmsCustomerId: CustomerCartAction;
  getHoldCart: HoldCartAction;
  resetCart: ResetCartAction;
  validatePaymentBatch: ValidatePaymentBatchAction;
  updateLineItem: UpdateLineItemAction;
  beginNoChargeOrder: NoChargeOrderAction;
  replaceCartLineItem: ReplaceCartLineItemAction;
  revalidateCart: RevalidatecartAction;
  submitSoaOrder: SubmitSoaOrderAction;
  handleShippingInsurance: HandleShippingInsuranceAction;
  getPriorPromoCodes: GetPriorPromoCodesAction;
};

export const getCartActions = (sdk: SDK<ComposableCommerceEvents>): CartActions => {
  return {
    getCart: async (options: { serverOptions?: ServerOptions; query?: { skipSetAvailability?: string } } = {}) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/getCart',
        serverOptions: options.serverOptions,
        query: {
          divisionCode: getSelectedDivision() ?? '',
          ...(options.query?.skipSetAvailability ? { skipSetAvailability: options.query.skipSetAvailability } : {}),
        },
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'cartFetched',
            data: {
              cart: response,
            },
          }),
        );
      }
      return response;
    },
    addItem: async (payload: AddCartItemPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const siteId = payload?.siteId;
      delete payload.siteId;
      const response = await callActionMiddleware<Cart>({
        actionName: siteId ? `cart/addToCart?siteId=${siteId}Site` : 'cart/addToCart',
        payload,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'productAddedToCart',
            data: {
              product: payload.variant,
              quantity: payload.variant.count,
            },
          }),
        );
      }
      return response;
    },
    replaceCartLineItem: async (
      payload: ReplaceCartLineItemPayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/replaceCartLineItem',
        payload,
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'productRemovedFromCart',
            data: {
              product: payload.lineItem,
              quantity: 1,
            },
          }),
        );
      }
      return response;
    },
    removeItem: async (payload: RemoveCartItemPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/removeLineItem',
        payload,
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'productRemovedFromCart',
            data: {
              product: payload.lineItem,
              quantity: 1,
            },
          }),
        );
      }
      return response;
    },
    updateItem: async (payload: UpdateCartItemPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/updateLineItem',
        payload,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'productUpdatedInCart',
            data: {
              product: {
                id: payload.lineItem.id,
              },
              newQuantity: payload.lineItem.count,
            },
          }),
        );
      }
      return response;
    },
    updateCart: async (payload: UpdateCartPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/updateCart',
        payload,
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'cartUpdated',
            data: payload,
          }),
        );
      }
      return response;
    },
    getShippingMethods: async (
      payload?: GetCartShippingMethodsPayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await callActionMiddleware<ShippingMethod[]>({
        actionName: 'cart/getShippingMethods',
        query: payload?.query ?? undefined,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'shippingMethodsFetched',
            data: {
              shippingMethods: response.data,
            },
          }),
        );
      }
      return response;
    },
    getAvailableShippingMethods: async (options: { serverOptions?: ServerOptions } = {}) => {
      const urlParams = new URLSearchParams(window.location.search);
      const selectedDivision = urlParams.get('divisionCode') || '';
      const response = await callActionMiddleware<ShippingMethod[]>({
        actionName: 'cart/getAvailableShippingMethods',
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? selectedDivision },
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'availableShippingMethodsFetched',
            data: {
              shippingMethods: response.data,
            },
          }),
        );
      }
      return response;
    },
    setShippingMethod: async (
      payload: SetCartShippingMethodPayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/setShippingMethod',
        payload,
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'shippingMethodUpdated',
            data: {
              shippingMethod: response.data.availableShippingMethods?.find(
                (shippingMethod) => shippingMethod.shippingMethodId === payload.shippingMethod.id,
              ),
            },
          }),
        );
      }
      return response;
    },
    redeemDiscountCode: async (payload: RedeemDiscountCodePayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<CartAndError>({
        actionName: 'cart/redeemDiscount',
        payload,
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'discountCodeRedeemed',
            data: {
              discountCode: typeof response.data !== 'string' ? payload.code : response.data,
              cart: typeof response.data !== 'string' ? response.data : undefined,
            },
          }),
        );
      }
      return response;
    },
    removeDiscountCode: async (payload: RemoveDiscountCodePayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/removeDiscount',
        payload,
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'discountCodeRemoved',
            data: {
              discountCode: payload.discountId,
              cart: response.data,
            },
          }),
        );
      }
      return response;
    },
    checkout: async (options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/checkout',
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? 'SM' },
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'cartCheckedOut',
            data: {},
          }),
        );
      }
      return response;
    },
    submitSoaOrder: async (
      payload?: GetCtOrderByIdPayload,
      options: {
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<SOASubmitOrderResponse>({
        actionName: 'cart/submitSoaOrder',
        payload,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    queryOrders: async (
      query?: QueryOrdersQuery,
      options: {
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await callActionMiddleware<PaginatedResult<Order>>({
        actionName: 'cart/queryOrders',
        query,
        serverOptions: options.serverOptions,
      });
      return response;
    },

    applyShippingDiscount: async (
      payload: ShippingDiscountPayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/applyShippingDiscount',
        serverOptions: options.serverOptions,
        payload,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });
      return response;
    },

    applyAdjustments: async (payload: AdjustmentPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/applyAdjustments',
        serverOptions: options.serverOptions,
        payload,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });
      return response;
    },
    freezeCart: async (options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/freezeCart',
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });
      return response;
    },
    unfreezeCart: async (options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/unfreezeCart',
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });
      return response;
    },
    adjustLineItemPrice: async (payload: AdjustPricePayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Cart>({
        actionName: 'cart/adjustLineItemPrice',
        serverOptions: options.serverOptions,
        payload,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });
      return response;
    },

    addAdditionalDetails: async (
      payload: AdditionalDetailsPayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await callActionMiddleware<any>({
        actionName: 'cart/addNotes',
        serverOptions: options.serverOptions,
        payload: { ...payload },
        query: { divisionCode: getSelectedDivision() ?? '' },
      });
      return response;
    },

    cancelOrder: async (options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<any>({
        actionName: 'cart/cancelOrder',
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });
      return response;
    },

    getCartsByOmsCustomerId: async (payload: UpdateCartPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<any>({
        actionName: 'cart/getCartsByOmsCustomerId',
        serverOptions: options.serverOptions,
        payload,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });
      return response;
    },
    getHoldCart: async (payload: any, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<any>({
        actionName: 'cart/getHoldCart',
        serverOptions: options.serverOptions,
        query: { ...payload.query },
      });
      return response;
    },

    resetCart: async (payload: ResetCartPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<any>({
        actionName: 'cart/resetCart',
        payload,
        serverOptions: options.serverOptions,
      });
      return response;
    },

    validatePaymentBatch: async (
      payload: ValidatePaymentBatchPayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await callActionMiddleware<any>({
        query: { ...payload?.query },
        payload: { ...payload?.data },
        actionName: 'soaCustomer/validatePaymentBatch',
        serverOptions: options.serverOptions,
      });
      return response;
    },

    updateLineItem: async (payload: UpdateLineItem, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<any>({
        payload: { ...payload?.data },
        actionName: 'cart/replaceCartLineItem',
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });
      return response;
    },

    beginNoChargeOrder: async (options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<any>({
        actionName: 'cart/noChargeOrder',
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });
      return response;
    },
    revalidateCart: async (options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<any>({
        actionName: 'cart/recalulateCart',
        serverOptions: options.serverOptions,
        query: { divisionCode: getSelectedDivision() ?? '' },
      });
      return response;
    },
    getLineItemsAvailibilty: async (options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Availability[]>({
        actionName: 'cart/getLineItemsAvailibilty',
        skipQueue: true,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    handleShippingInsurance: async (payload: InsuranceItemPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/handleShippingInsurance',
        payload,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    getPriorPromoCodes: async (
      query: GetPriorPromoCodesQuery = {},
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await callActionMiddleware<PriorPromoCodesResult>({
        actionName: 'cart/getPriorPromoCodes',
        query: {
          status: query.status || 'valid',
          page: query.page || 1,
          pageSize: query.pageSize || 20,
          sortBy: query.sortBy || 'validFrom',
          sortDir: query.sortDir || 'desc',
          divisionCode: getSelectedDivision() ?? '',
          ...(query.search ? { search: query.search } : {}),
          ...(query.expiredDays !== undefined ? { expiredDays: query.expiredDays } : {}),
          ...(query.futureDays !== undefined ? { futureDays: query.futureDays } : {}),
        },
        serverOptions: options.serverOptions,
      });
      return response;
    },
  };
};
